"use client";
import React, { memo } from "react";

import { cn } from "@/utils/cn";
import Link from "next/link";

interface TextControlProps {
  label?: string;
  value?: string | number | boolean | string[];
  children?: React.ReactNode;
  error?: boolean;
  required?: boolean;
  className?: string;
  valueClassName?: string;
  sizeVariant?: "default" | "small";
  links?: boolean;
}

/**
 * Component for displaying read-only text information
 */
const TextControl: React.FC<TextControlProps> = memo(
  ({
    label,
    value,
    children,
    error = false,
    required = false,
    className,
    valueClassName,
    links = false,
    sizeVariant = "default",
  }) => {
    const isArray = Array.isArray(value);

    const renderedContent = React.useMemo(() => {
      if (label && value !== undefined && !isArray) {
        return (
          <div className="flex flex-col items-start justify-start ">
            <span className="text-gray-600 mr-1 mb-[1.3rem] text-[18px] font-medium leading-[1.26]">
              {label}
            </span>
            {links ? (
              <Link
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all"
              >
                {value}
              </Link>
            ) : (
              <span
                className={cn(
                  "font-semibold text-[#323232] text-[18px] leading-[1.26] whitespace-normal break-words [overflow-wrap:anywhere]",
                  valueClassName,
                )}
              >
                {value}
              </span>
            )}
          </div>
        );
      }

      if (label && value !== undefined && isArray && value.length > 0) {
        return (
          <div className="flex flex-col items-start justify-start gap-4">
            <span className="text-gray-600 mr-1 mb-[1.3rem] text-[18px] font-medium leading-[1.26]">
              {label}
            </span>
            {value.map((item) =>
              links ? (
                <Link
                  key={item}
                  href={item}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all"
                >
                  {item}
                </Link>
              ) : (
                <span
                  key={item}
                  className={cn(
                    "font-semibold text-[#323232] text-[18px] leading-[1.26]",
                    valueClassName,
                  )}
                >
                  {item}
                </span>
              ),
            )}
          </div>
        );
      }
      return children;
    }, [label, value, children, valueClassName, isArray]);

    return (
      <div
        className={cn(
          "form-text-control",
          error && "form-text-control-error",
          className,
        )}
      >
        {renderedContent}
        {required && <span className="form-text-control-required">*</span>}
      </div>
    );
  },
);

// Display name for debugging
TextControl.displayName = "TextControl";

export default TextControl;
